"use client";
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme,
  Button
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import GavelIcon from "@mui/icons-material/Gavel";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EmailIcon from "@mui/icons-material/Email";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import ArchiveIcon from "@mui/icons-material/Archive";
import SearchIcon from "@mui/icons-material/Search";
import ChatIcon from "@mui/icons-material/Chat";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../Component/LanguageProvider";

export const OurservicePage = () => {
  const navigate = useNavigate();
  const { currentLang, isInitialized } = useLanguage();
  const [selectedService, setSelectedService] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isRTL = currentLang === "ar";

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  // Translations
  const translations = {
    en: {
      title: "Our Services",
      subtitle: "RUKN ALMUROOJ Documents Clearing Services",
      note: "Note: We do not provide legal advice or legal drafting. Any legal documents are printed based on texts provided by the client",
      clickForMore: "Click for more",
      dialogIncludes: "Includes:",
      backToHome: "Back to Home",
      servicesData: [
        {
          id: 1,
          title: "Rental Dispute Center Requests Follow-up",
          shortDesc: "Electronic submission and follow-up of requests with high accuracy",
          longDesc:
            "Rental Dispute Center Requests Follow-up service includes electronic submission of requests, procedure follow-up, and inquiry about request status. We provide full administrative support to clients to ensure their requests proceed smoothly.",
          details: [
            "Electronic request submission",
            "Regular procedure follow-up",
            "Request status inquiry",
            "Client notification of updates",
          ],
          note: "This is an administrative service only, does not include legal representation or court pleading.",
        },
        {
          id: 2,
          title: "Government Transactions Follow-up",
          shortDesc: "Submitting requests according to approved systems and regulations",
          longDesc:
            "Government Transactions Follow-up service includes submitting requests according to approved systems and regulations, procedure follow-up, and receiving completed transactions.",
          details: [
            "Submitting requests according to regulations",
            "Government procedure follow-up",
            "Receiving completed transactions",
            "Client update with latest developments",
          ],
          note: "This is an administrative service only, does not include legal representation.",
        },
        {
          id: 3,
          title: "Form & Case Request Filling",
          shortDesc: "Based on client-provided data with high accuracy",
          longDesc:
            "Form and case request filling service includes filling all required forms with high accuracy based on client-provided data, with comprehensive review to ensure data accuracy.",
          details: [
            "Accurate form filling",
            "Data entry review",
            "Information verification",
            "Request preparation for submission",
          ],
          note: "This is an administrative service only, form filling based on client data.",
        },
        {
          id: 4,
          title: "Document Preparation",
          shortDesc: "Printing, copying, and professional file formatting",
          longDesc:
            "Document preparation service includes document printing, copying, professional file formatting, and arranging them according to relevant authority requirements.",
          details: [
            "Document printing",
            "Document copying",
            "File formatting",
            "Document arrangement according to requirements",
          ],
          note: "This is an administrative service only, does not include legal drafting.",
        },
        {
          id: 5,
          title: "Attestation Request Follow-up",
          shortDesc: "With competent authorities and procedure follow-up",
          longDesc:
            "Attestation request follow-up service includes submitting attestation requests to competent authorities, procedure follow-up, and receiving documents after attestation.",
          details: [
            "Attestation request submission",
            "Procedure follow-up",
            "Document receipt after attestation",
            "Document delivery to client",
          ],
          note: "This is an administrative service only, does not include legal representation.",
        },
        {
          id: 6,
          title: "Document Archiving & Organization",
          shortDesc: "Organizing and preserving files in a safe and organized manner",
          longDesc:
            "Document archiving and organization services include file organization, safe preservation, and indexing to facilitate access when needed.",
          details: [
            "File organization",
            "Safe document preservation",
            "Document indexing",
            "Easy access to documents",
          ],
          note: "This is an archiving and organization service only, does not include legal consultations.",
        },
        {
          id: 7,
          title: "Case Status Inquiry",
          shortDesc: "Notifying client of updates promptly",
          longDesc:
            "Case status inquiry service includes electronic case status follow-up, inquiry about latest updates, and notifying client of any developments.",
          details: [
            "Case status follow-up",
            "Update inquiry",
            "Client notification of developments",
            "Periodic report submission",
          ],
          note: "This is an inquiry service only, does not include legal representation.",
        },
        {
          id: 8,
          title: "Letter & Correspondence Preparation",
          shortDesc: "Based on client-provided texts with professionalism",
          longDesc:
            "Letter and correspondence preparation service includes preparing official letters and administrative correspondence based on client-provided texts, with attention to accuracy and professionalism.",
          details: [
            "Official letter preparation",
            "Administrative correspondence preparation",
            "Text review",
            "Ready-to-sign letter delivery",
          ],
          note: "This is an administrative service only, does not include legal drafting or consultations.",
        },
      ],
    },
    ar: {
      title: "خدماتنا",
      subtitle: "ركن المروج لخدمات تصفية المعاملات",
      note: "ملاحظة: لا نقدم استشارات قانونية ولا صياغة قانونية، وأي مستندات قانونية يتم طباعتها بناءً على النصوص التي يقدمها العميل",
      clickForMore: "اضغط للمزيد",
      dialogIncludes: "تشمل:",
      backToHome: "الرجوع للرئيسية",
      servicesData: [
        {
          id: 1,
          title: "متابعة طلبات مركز فض المنازعات الإيجارية",
          shortDesc: "رفع الطلبات إلكترونياً ومتابعتها بدقة واحترافية",
          longDesc:
            "خدمة متابعة طلبات مركز فض المنازعات الإيجارية تشمل رفع الطلبات إلكترونياً، متابعة الإجراءات، والاستعلام عن حالة الطلبات. نقدم دعماً إدارياً كاملاً للعملاء لضمان سير طلباتهم بسلاسة.",
          details: [
            "رفع الطلبات إلكترونياً",
            "متابعة الإجراءات بشكل دوري",
            "الاستعلام عن حالة الطلبات",
            "إبلاغ العميل بالتحديثات أولاً بأول",
          ],
          note: "هذه خدمة إدارية فقط، لا تشمل التمثيل القانوني أو الترافع أمام المحاكم.",
        },
        {
          id: 2,
          title: "متابعة المعاملات الحكومية",
          shortDesc: "تقديم الطلبات وفق الأنظمة واللوائح المعتمدة",
          longDesc:
            "خدمة متابعة المعاملات الحكومية تشمل تقديم الطلبات وفق الأنظمة واللوائح المعتمدة، متابعة الإجراءات، واستلام المعاملات بعد إنجازها.",
          details: [
            "تقديم الطلبات وفق الأنظمة",
            "متابعة الإجراءات الحكومية",
            "استلام المعاملات بعد الإنجاز",
            "تحديث العميل بآخر المستجدات",
          ],
          note: "هذه خدمة إدارية فقط، لا تشمل التمثيل القانوني.",
        },
        {
          id: 3,
          title: "تعبئة النماذج وطلبات القضايا",
          shortDesc: "بناءً على بيانات يقدمها العميل بدقة عالية",
          longDesc:
            "خدمة تعبئة النماذج وطلبات القضايا تشمل تعبئة جميع الاستمارات المطلوبة بدقة عالية بناءً على البيانات التي يقدمها العميل، مع مراجعة شاملة للتأكد من صحة البيانات.",
          details: [
            "تعبئة النماذج بدقة",
            "مراجعة البيانات المدخلة",
            "التأكد من صحة المعلومات",
            "تجهيز الطلبات للإيداع",
          ],
          note: "هذه خدمة إدارية فقط، تعبئة النماذج بناءً على بيانات العميل.",
        },
        {
          id: 4,
          title: "تجهيز المستندات",
          shortDesc: "طباعتها ونسخها وتنسيق الملفات بشكل احترافي",
          longDesc:
            "خدمة تجهيز المستندات تشمل طباعة المستندات، نسخها، تنسيق الملفات بشكل احترافي، وترتيبها حسب متطلبات الجهات المعنية.",
          details: [
            "طباعة المستندات",
            "نسخ المستندات",
            "تنسيق الملفات",
            "ترتيب المستندات حسب المتطلبات",
          ],
          note: "هذه خدمة إدارية فقط، لا تشمل صياغة قانونية.",
        },
        {
          id: 5,
          title: "متابعة طلبات التصديق",
          shortDesc: "لدى الجهات المختصة ومتابعة الإجراءات",
          longDesc:
            "خدمة متابعة طلبات التصديق تشمل تقديم طلبات التصديق للجهات المختصة، متابعة الإجراءات، واستلام المستندات بعد التصديق.",
          details: [
            "تقديم طلبات التصديق",
            "متابعة الإجراءات",
            "استلام المستندات بعد التصديق",
            "تسليم المستندات للعميل",
          ],
          note: "هذه خدمة إدارية فقط، لا تشمل التمثيل القانوني.",
        },
        {
          id: 6,
          title: "خدمات أرشفة وتنظيم المستندات",
          shortDesc: "تنظيم وحفظ الملفات بطريقة آمنة ومنظمة",
          longDesc:
            "خدمات أرشفة وتنظيم المستندات تشمل تنظيم الملفات، حفظها بطريقة آمنة، وفهرستها لتسهيل الوصول إليها عند الحاجة.",
          details: [
            "تنظيم الملفات",
            "حفظ المستندات بأمان",
            "فهرسة المستندات",
            "تسهيل الوصول إلى المستندات",
          ],
          note: "هذه خدمة أرشفة وتنظيم فقط، لا تشمل استشارات قانونية.",
        },
        {
          id: 7,
          title: "الاستعلام عن حالة القضايا",
          shortDesc: "إبلاغ العميل بالتحديثات أولاً بأول",
          longDesc:
            "خدمة الاستعلام عن حالة القضايا تشمل متابعة حالة القضايا إلكترونياً، الاستعلام عن آخر التحديثات، وإبلاغ العميل بأي تطورات.",
          details: [
            "متابعة حالة القضايا",
            "الاستعلام عن التحديثات",
            "إبلاغ العميل بأي تطورات",
            "تقديم تقارير دورية",
          ],
          note: "هذه خدمة استعلامية فقط، لا تشمل تمثيلاً قانونياً.",
        },
        {
          id: 8,
          title: "إعداد الخطابات والمراسلات",
          shortDesc: "بناءً على نصوص يقدمها العميل وباحترافية",
          longDesc:
            "خدمة إعداد الخطابات والمراسلات تشمل إعداد الخطابات الرسمية والمراسلات الإدارية بناءً على النصوص التي يقدمها العميل، مع مراعاة الدقة والاحترافية.",
          details: [
            "إعداد الخطابات الرسمية",
            "إعداد المراسلات الإدارية",
            "مراجعة النصوص المدخلة",
            "تسليم الخطابات جاهزة للتوقيع",
          ],
          note: "هذه خدمة إدارية فقط، لا تشمل صياغة قانونية أو استشارات.",
        },
      ],
    },
  };

  const t = translations[currentLang];
  const servicesData = t.servicesData;

  // Icon mapping
  const getIcon = (id) => {
    const icons = {
      1: GavelIcon,
      2: BusinessIcon,
      3: AssignmentIcon,
      4: DescriptionIcon,
      5: VerifiedIcon,
      6: ArchiveIcon,
      7: SearchIcon,
      8: ChatIcon,
    };
    return icons[id] || GavelIcon;
  };

  // Color mapping
  const getColor = (id) => {
    const colors = {
      1: "#d4af37",
      2: "#b8942f",
      3: "#9e7c2a",
      4: "#8b6b1f",
      5: "#6b4f15",
      6: "#5a3e10",
      7: "#d4af37",
      8: "#b8942f",
    };
    return colors[id] || "#d4af37";
  };

  const handleOpenDialog = (service) => {
    setSelectedService(service);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  const getCardWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "calc(50% - 16px)";
    return "calc(25% - 24px)";
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <Box
      id="services"
      sx={{
        minHeight: "100vh",
        py: { xs: 8, md: 12 },
        background: "linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%)",
        position: "relative",
        overflow: "hidden",
        pt: { xs: 12, md: 14 },
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Back Button */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mb: 4 }}>
              <Button
                onClick={() => navigate("/")}
                sx={{
                  color: "#c9a03d",
                  borderColor: "#c9a03d",
                  borderRadius: "30px",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#c9a03d",
                    color: "white",
                  },
                }}
                variant="outlined"
                startIcon={isRTL ? null : <ArrowBackIcon />}
                endIcon={isRTL ? <ArrowBackIcon /> : null}
              >
                {t.backToHome}
              </Button>
            </Box>
          </motion.div>

          {/* Section Title */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                  fontSize: { xs: "1.8rem", sm: "2rem", md: "2.5rem" },
                }}
              >
                {t.title}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  color: "#d4af37",
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "1.5rem" },
                }}
              >
                {t.subtitle}
              </Typography>
              <Divider
                sx={{
                  width: "80px",
                  margin: "0 auto",
                  height: "4px",
                  background: "linear-gradient(90deg, #d4af37, #b8942f)",
                  borderRadius: "2px",
                }}
              />
            </Box>
          </motion.div>

          {/* Services Cards */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 2, sm: 3, md: 3 },
              mb: 6,
            }}
          >
            {servicesData.map((service, index) => {
              const Icon = getIcon(service.id);
              const serviceColor = getColor(service.id);
              return (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                  whileHover={{
                    y: -10,
                    transition: { type: "spring", stiffness: 300 },
                  }}
                  style={{
                    width: getCardWidth(),
                    maxWidth: isMobile ? "100%" : "300px",
                    minWidth: isMobile ? "auto" : "250px",
                  }}
                >
                  <Paper
                    elevation={3}
                    onClick={() => handleOpenDialog(service)}
                    sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      height: "100%",
                      minHeight: { xs: 260, sm: 280, md: 300 },
                      display: "flex",
                      flexDirection: "column",
                      background: "white",
                      borderRadius: "20px",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      border: "2px solid transparent",
                      "&:hover": {
                        borderColor: serviceColor,
                        boxShadow: `0 20px 40px ${serviceColor}20`,
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `linear-gradient(90deg, ${serviceColor}, ${serviceColor}dd)`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        height: "100%",
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "transparent",
                          border: `2px solid ${serviceColor}`,
                          width: { xs: 50, sm: 55, md: 60 },
                          height: { xs: 50, sm: 55, md: 60 },
                          mb: 2,
                          "& svg": {
                            color: serviceColor,
                            fontSize: { xs: 25, sm: 28, md: 30 },
                          },
                        }}
                      >
                        <Icon />
                      </Avatar>

                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Cairo, Roboto",
                          fontWeight: "bold",
                          color: "#1a1a2e",
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                          lineHeight: 1.4,
                          mb: 1.5,
                          minHeight: { xs: 40, sm: 45, md: 50 },
                          display: "flex",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        {service.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Cairo, Roboto",
                          color: "#666",
                          lineHeight: 1.6,
                          flex: 1,
                          mb: 2,
                          fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                          textAlign: "center",
                        }}
                      >
                        {service.shortDesc}
                      </Typography>

                      <Chip
                        label={t.clickForMore}
                        size="small"
                        sx={{
                          bgcolor: serviceColor,
                          color: "white",
                          fontSize: "0.65rem",
                          "&:hover": { opacity: 0.9 },
                          height: { xs: 24, sm: 26 },
                        }}
                      />
                    </Box>
                  </Paper>
                </motion.div>
              );
            })}
          </Box>

          {/* Legal Note */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: { xs: 2, sm: 2.5, md: 3 },
                background: "rgba(212,175,55,0.1)",
                borderRadius: "12px",
                border: "1px solid rgba(212,175,55,0.3)",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <WarningIcon sx={{ color: "#d4af37", fontSize: { xs: 18, sm: 20 } }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Cairo, Roboto",
                    color: "#666",
                    lineHeight: 1.6,
                    fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.85rem" },
                  }}
                >
                  {t.note}
                </Typography>
              </Box>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>

      {/* Service Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={400}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            background: "white",
            boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        {selectedService && (
          <>
            <DialogTitle
              sx={{
                m: 0,
                p: { xs: 2, sm: 3 },
                display: "flex",
                alignItems: "center",
                borderBottom: `3px solid ${getColor(selectedService.id)}`,
                fontFamily: "Cairo, Roboto",
                fontWeight: "bold",
                fontSize: { xs: "1rem", sm: "1.2rem" },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "transparent",
                  border: `2px solid ${getColor(selectedService.id)}`,
                  width: { xs: 35, sm: 40 },
                  height: { xs: 35, sm: 40 },
                  [isRTL ? "ml" : "mr"]: 2,
                  "& svg": {
                    color: getColor(selectedService.id),
                    fontSize: { xs: 18, sm: 20 },
                  },
                }}
              >
                {React.createElement(getIcon(selectedService.id))}
              </Avatar>
              {selectedService.title}
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  position: "absolute",
                  [isRTL ? "left" : "right"]: 8,
                  top: 8,
                  color: "#999",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontFamily: "Cairo, Roboto",
                  color: "#1a1a2e",
                  lineHeight: 1.8,
                  mb: 3,
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                }}
              >
                {selectedService.longDesc}
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                }}
              >
                {t.dialogIncludes}
              </Typography>

              <Box sx={{ mb: 3 }}>
                {selectedService.details.map((detail, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1.5,
                      flexDirection: isRTL ? "row-reverse" : "row",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: getColor(selectedService.id),
                        [isRTL ? "ml" : "mr"]: 2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Cairo, Roboto",
                        color: "#666",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    >
                      {detail}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  background: "rgba(212,175,55,0.1)",
                  borderRadius: "12px",
                  border: "1px solid rgba(212,175,55,0.3)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <WarningIcon sx={{ color: getColor(selectedService.id), fontSize: 18, mt: 0.2 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "Cairo, Roboto",
                      color: "#666",
                      lineHeight: 1.6,
                      fontSize: "0.7rem",
                    }}
                  >
                    {selectedService.note}
                  </Typography>
                </Box>
              </Paper>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OurservicePage;